import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  googleReviewsApi,
  type GoogleBusinessLocation,
  type GoogleReviewConfig,
} from '@/lib/api/google-reviews.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastInfo, toastSuccess } from '@/lib/toast.js';

const mergeFields = ['{customer_name}', '{device}', '{repair}', '{store_name}', '{review_link}'];

export function GoogleReviewsSettingsSection() {
  const queryClient = useQueryClient();
  const configQuery = useQuery({
    queryKey: ['google-reviews-config'],
    queryFn: googleReviewsApi.getConfig,
  });

  const [form, setForm] = useState<Partial<GoogleReviewConfig>>({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [testPhone, setTestPhone] = useState('');

  const config = configQuery.data?.data;

  const locationsQuery = useQuery({
    queryKey: ['google-reviews-locations'],
    queryFn: googleReviewsApi.listLocations,
    enabled: pickerOpen,
  });

  useEffect(() => {
    if (config) {
      setForm({
        reviewRequestEnabled: config.reviewRequestEnabled,
        reviewRequestDelayDays: config.reviewRequestDelayDays,
        reviewRequestMessage: config.reviewRequestMessage ?? '',
        satisfiedMessage: config.satisfiedMessage ?? '',
        unsatisfiedMessage: config.unsatisfiedMessage ?? '',
        googlePlaceId: config.placeId ?? config.googlePlaceId,
        reviewLinkUrl: config.reviewLink ?? config.reviewLinkUrl,
        npsEnabled: config.npsEnabled,
        npsDelayDays: config.npsDelayDays,
        npsSurveyMessage: config.npsSurveyMessage ?? '',
        npsPromoterMessage: config.npsPromoterMessage ?? '',
        npsPassiveMessage: config.npsPassiveMessage ?? '',
        npsDetractorMessage: config.npsDetractorMessage ?? '',
        npsGoogleThreshold: config.npsGoogleThreshold,
      });
    }
  }, [config]);

  const saveMut = useMutation({
    mutationFn: () => googleReviewsApi.patchConfig(form),
    onSuccess: () => {
      toastSuccess('Review settings saved');
      queryClient.invalidateQueries({ queryKey: ['google-reviews-config'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const connectMut = useMutation({
    mutationFn: async () => {
      const res = await googleReviewsApi.getAuthUrl();
      window.location.href = res.data.url;
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const disconnectMut = useMutation({
    mutationFn: googleReviewsApi.disconnect,
    onSuccess: () => {
      toastSuccess('Google account disconnected');
      queryClient.invalidateQueries({ queryKey: ['google-reviews-config'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const saveLocationMut = useMutation({
    mutationFn: (loc: GoogleBusinessLocation) =>
      googleReviewsApi.saveLocation({
        accountId: loc.accountId,
        locationId: loc.locationId,
        locationName: loc.locationName ?? undefined,
        placeId: loc.placeId,
      }),
    onSuccess: () => {
      toastSuccess('Google Business Profile connected!');
      setPickerOpen(false);
      setSelectedKey(null);
      queryClient.invalidateQueries({ queryKey: ['google-reviews-config'] });
      queryClient.invalidateQueries({ queryKey: ['google-reviews-stats'] });
      queryClient.invalidateQueries({ queryKey: ['google-reviews-list'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const testMut = useMutation({
    mutationFn: (phone: string) => googleReviewsApi.testReviewRequest(phone),
    onSuccess: () => toastSuccess('Test review request sent'),
    onError: (e) => toastError(parseApiError(e).message),
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const google = params.get('google');
    if (google === 'pick_location') {
      window.history.replaceState({}, '', window.location.pathname);
      queryClient.invalidateQueries({ queryKey: ['google-reviews-config'] });
      setPickerOpen(true);
    } else if (google === 'connected') {
      toastSuccess('Google Business Profile connected');
      window.history.replaceState({}, '', window.location.pathname);
      queryClient.invalidateQueries({ queryKey: ['google-reviews-config'] });
    } else if (google === 'error') {
      const reason = decodeURIComponent(params.get('reason') ?? 'unknown');
      const hints: Record<string, string> = {
        enable_google_apis:
          'Enable “My Business Account Management API” and “My Business Business Information API” in Google Cloud Console for this OAuth project, then try again.',
        redirect_uri_mismatch:
          'Redirect URI mismatch — authorized URI must be exactly http://localhost:3001/api/v1/auth/google/callback',
        no_business_location:
          'No Google Business Profile location found on this account. Sign in with the account that owns the listing.',
        insufficient_scope:
          'Missing Google Business permissions — disconnect and reconnect, granting all requested access.',
        invalid_grant: 'Authorization code expired — click Connect again.',
        token_exchange_failed:
          'Could not exchange OAuth code for tokens. Check Client ID/Secret and redirect URI.',
        discovery_failed: 'Connected to Google but could not load Business Profile accounts/locations.',
        save_failed: 'Got Google tokens but failed saving to the database.',
      };
      toastError(hints[reason] ?? `Google connection failed: ${reason}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [queryClient]);

  function openPicker() {
    setSelectedKey(null);
    setPickerOpen(true);
  }

  function handleCancelPicker() {
    setPickerOpen(false);
    setSelectedKey(null);
    if (!config?.connected) {
      toastInfo(
        'Google connected but no location selected. Reviews won’t work until you select a location.',
      );
    }
  }

  function handleSaveLocation() {
    const locations = locationsQuery.data?.data ?? [];
    const loc = locations.find((l) => `${l.accountId}|${l.locationId}` === selectedKey);
    if (!loc) {
      toastError('Select a location first');
      return;
    }
    saveLocationMut.mutate(loc);
  }

  const placeId = config?.placeId ?? config?.googlePlaceId;
  const reviewLink = config?.reviewLink ?? config?.reviewLinkUrl;
  const locations = locationsQuery.data?.data ?? [];

  return (
    <section className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-6">
      <h3 className="text-lg font-bold text-slate-950">Google Reviews</h3>

      <div className="rounded-xl bg-white p-4">
        <p className="text-sm font-semibold text-slate-800">Google Business Profile</p>

        {config?.connected ? (
          <div className="mt-2 space-y-1 text-sm text-slate-600">
            <p>🟢 Google Business Profile Connected</p>
            {config.locationName ? <p>Location: {config.locationName}</p> : null}
            {config.locationAddress ? <p>Address: {config.locationAddress}</p> : null}
            {placeId ? <p className="text-xs text-slate-500">Place ID: {placeId}</p> : null}
            {reviewLink ? (
              <div className="flex flex-wrap items-center gap-2">
                <p className="break-all text-xs text-slate-500">Review link: {reviewLink}</p>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-2 py-0.5 text-xs font-semibold"
                  onClick={() => {
                    void navigator.clipboard.writeText(reviewLink);
                    toastSuccess('Review link copied');
                  }}
                >
                  Copy
                </button>
              </div>
            ) : null}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={openPicker}
              >
                Change Location
              </button>
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={() => disconnectMut.mutate()}
              >
                Disconnect
              </button>
            </div>
          </div>
        ) : config?.needsLocation ? (
          <div className="mt-2 space-y-3 text-sm text-slate-600">
            <p>🟡 Google Connected — Location Needed</p>
            <p>Google account connected but no location selected yet.</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                onClick={openPicker}
              >
                Select Location →
              </button>
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={() => disconnectMut.mutate()}
              >
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-2 space-y-3">
            <p className="text-sm text-slate-600">🔴 Not Connected</p>
            <button
              type="button"
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
              onClick={() => connectMut.mutate()}
              disabled={connectMut.isPending}
            >
              Connect Google Business Profile →
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3 rounded-xl bg-white p-4">
        <p className="font-semibold text-slate-950">Review request automation</p>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={Boolean(form.reviewRequestEnabled)}
            onChange={(e) => setForm((f) => ({ ...f, reviewRequestEnabled: e.target.checked }))}
          />
          Enable automatic review requests
        </label>
        <label className="block text-sm">
          Send request (days after ticket closes)
          <input
            type="number"
            min={1}
            max={30}
            className="mt-1 w-24 rounded-xl border border-slate-300 px-3 py-2"
            value={form.reviewRequestDelayDays ?? 1}
            onChange={(e) =>
              setForm((f) => ({ ...f, reviewRequestDelayDays: Number(e.target.value) }))
            }
          />
        </label>
        <p className="text-xs text-slate-500">Merge fields: {mergeFields.join(' ')}</p>
        <label className="block text-sm">
          Initial message template
          <textarea
            className="mt-1 min-h-20 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={form.reviewRequestMessage ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, reviewRequestMessage: e.target.value }))}
            maxLength={500}
          />
          <span className="text-xs text-slate-500">
            {(form.reviewRequestMessage ?? '').length} / 160 recommended
          </span>
        </label>
        <label className="block text-sm">
          Satisfied response template
          <textarea
            className="mt-1 min-h-16 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={form.satisfiedMessage ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, satisfiedMessage: e.target.value }))}
          />
        </label>
        <label className="block text-sm">
          Unsatisfied response template
          <textarea
            className="mt-1 min-h-16 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={form.unsatisfiedMessage ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, unsatisfiedMessage: e.target.value }))}
          />
        </label>
        <label className="block text-sm">
          Google Place ID (optional — auto-generates review link)
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={form.googlePlaceId ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, googlePlaceId: e.target.value || null }))}
          />
        </label>
        <div className="flex flex-wrap items-end gap-2">
          <label className="block text-sm">
            Send test to phone
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="+15551234567"
            />
          </label>
          <button
            type="button"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-50"
            disabled={testMut.isPending || testPhone.trim().length < 7}
            onClick={() => testMut.mutate(testPhone.trim())}
          >
            Send Test Request
          </button>
        </div>
        <button
          type="button"
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          disabled={saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          Save Review Settings
        </button>
      </div>

      <div className="space-y-3 rounded-xl bg-white p-4">
        <p className="font-semibold text-slate-950">NPS Surveys</p>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={Boolean(form.npsEnabled)}
            onChange={(e) => setForm((f) => ({ ...f, npsEnabled: e.target.checked }))}
          />
          Enable NPS surveys after ticket close
        </label>
        <label className="block text-sm">
          Send NPS survey (days after ticket closes)
          <input
            type="number"
            min={1}
            max={30}
            className="mt-1 w-24 rounded-xl border border-slate-300 px-3 py-2"
            value={form.npsDelayDays ?? 1}
            onChange={(e) => setForm((f) => ({ ...f, npsDelayDays: Number(e.target.value) }))}
          />
        </label>
        <label className="block text-sm">
          Google review threshold (score ≥)
          <input
            type="number"
            min={1}
            max={10}
            className="mt-1 w-24 rounded-xl border border-slate-300 px-3 py-2"
            value={form.npsGoogleThreshold ?? 9}
            onChange={(e) => setForm((f) => ({ ...f, npsGoogleThreshold: Number(e.target.value) }))}
          />
        </label>
        <label className="block text-sm">
          NPS survey message
          <textarea
            className="mt-1 min-h-16 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={form.npsSurveyMessage ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, npsSurveyMessage: e.target.value }))}
          />
        </label>
        <label className="block text-sm">
          Promoter follow-up message
          <textarea
            className="mt-1 min-h-16 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={form.npsPromoterMessage ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, npsPromoterMessage: e.target.value }))}
          />
        </label>
        <label className="block text-sm">
          Passive follow-up message
          <textarea
            className="mt-1 min-h-16 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={form.npsPassiveMessage ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, npsPassiveMessage: e.target.value }))}
          />
        </label>
        <label className="block text-sm">
          Detractor follow-up message
          <textarea
            className="mt-1 min-h-16 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            value={form.npsDetractorMessage ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, npsDetractorMessage: e.target.value }))}
          />
        </label>
        <button
          type="button"
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          disabled={saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          Save NPS Settings
        </button>
      </div>

      {pickerOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="google-location-picker-title"
            className="max-h-[90vh] w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl"
          >
            <div className="border-b border-slate-200 px-6 py-4">
              <h4 id="google-location-picker-title" className="text-lg font-bold text-slate-950">
                Select Your Google Business Location
              </h4>
              <p className="mt-1 text-sm text-slate-600">
                We found these locations on your Google account. Select the one that matches this
                store.
              </p>
            </div>

            <div className="max-h-[50vh] space-y-2 overflow-y-auto px-6 py-4">
              {locationsQuery.isLoading ? (
                <p className="text-sm text-slate-500">Loading locations…</p>
              ) : locationsQuery.isError ? (
                <p className="text-sm text-red-600">
                  {parseApiError(locationsQuery.error).message}
                </p>
              ) : locations.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No locations found on this Google account. Make sure you signed in with the account
                  that owns the Business Profile.
                </p>
              ) : (
                locations.map((loc) => {
                  const key = `${loc.accountId}|${loc.locationId}`;
                  return (
                    <label
                      key={key}
                      className={[
                        'flex cursor-pointer gap-3 rounded-xl border p-4 transition',
                        selectedKey === key
                          ? 'border-primary bg-primary/5'
                          : 'border-slate-200 hover:border-slate-300',
                      ].join(' ')}
                    >
                      <input
                        type="radio"
                        name="google-location"
                        className="mt-1"
                        checked={selectedKey === key}
                        onChange={() => setSelectedKey(key)}
                      />
                      <span className="min-w-0">
                        <span className="block font-semibold text-slate-950">
                          {loc.locationName ?? loc.locationId}
                        </span>
                        {loc.address ? (
                          <span className="mt-0.5 block text-sm text-slate-600">{loc.address}</span>
                        ) : null}
                        {loc.accountName ? (
                          <span className="mt-0.5 block text-xs text-slate-500">
                            Account: {loc.accountName}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                onClick={handleCancelPicker}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                disabled={!selectedKey || saveLocationMut.isPending}
                onClick={handleSaveLocation}
              >
                Save This Location →
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
